import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import api from "../lib/api";

const FormContext = createContext(null);

const initialData = {
  richiedente: { tipo: "", email: "", agenzia_nome: "", agenzia_referente: "" },
  locatori: [{ nome: "", email: "" }],
  conduttori: [{ nome: "" }],
  locatore: { nome: "", email: "" },       // legacy mirror of locatori[0]
  conduttore: { nome: "", email: "" },     // legacy mirror of conduttori[0]
  zona: 0,
  immobile: { via: "", civico: "", piano: "", ascensore: false, cap: "", citta: "Milano", provincia: "MI" },
  catasto: { foglio: "", particella: "", subalterno: "", categoria: "", rendita: "", mq_appartamento: "", pertinenze: [], pertinenze_incluse: [] },
  contratto: { tipologia: "", durata_valore: "", data_inizio: "", data_fine: "" },
  ape_scadenza: "",     // ISO date string
  visura_mq_ok: false,   // user must confirm visura contains mq indication
  associazione_inquilini: "uniat",  // selected sindacato inquilini per Step 11 (default: Uniat)
  valutazione: {}, // q1: 'si'|'no'
  superfici: {
    superficie_totale: "",
    spazi_comuni: "",
    in_stanze: "",
    box_garage: "",
    posto_auto: "",
    balconi_terrazzi: "",
    cantina: "",
    superficie_scoperta: "",
    esclusivo: {
      box_garage: "",
      posto_auto: "",
      balconi_terrazzi: "",
      cantina: "",
      superficie_scoperta: "",
    },
    esclusivo_mode: {
      box_garage: "comune",
      posto_auto: "comune",
      balconi_terrazzi: "comune",
      cantina: "comune",
      superficie_scoperta: "comune",
    },
    locazione_porzione: false,
    maggiorazione_45mq: false,
    // Elenco camere da letto (richiesto SOLO quando `locazione_porzione` = true).
    // Ogni camera: { id, nome, mq, locata }. Esattamente una camera può essere
    // marcata come `locata = true` → quella effettivamente locata al conduttore.
    camere: [],
  },
  step9: {
    ammobiliato: "no",
    classe_energetica: "",
    cantina_soffitta: "no",
    seminterrato: "no",
    canone_convenuto: "",
    maggiore_durata: "nessuna",
  },
  manleva_accettata: false,
  registrazione_ade: null, // null = non risposto, true = sì (€25), false = no
  firme: { firma_locatore: "", firma_conduttore: "" },
};

export function FormProvider({ children }) {
  const [data, setData] = useState(() => {
    try {
      const cached = localStorage.getItem("cc_form_data");
      if (cached) {
        const parsed = JSON.parse(cached);
        // Migration: derive locatori/conduttori arrays from legacy single fields
        // if the cached payload predates the multi-party feature.
        if (!Array.isArray(parsed.locatori)) {
          parsed.locatori = [{
            nome: parsed.locatore?.nome || "",
            email: parsed.locatore?.email || "",
          }];
        }
        if (!Array.isArray(parsed.conduttori)) {
          parsed.conduttori = [{ nome: parsed.conduttore?.nome || "" }];
        }
        // Migration: ensure superfici.esclusivo exists for locazione parziale support
        if (parsed.superfici && !parsed.superfici.esclusivo) {
          parsed.superfici.esclusivo = {
            box_garage: "", posto_auto: "", balconi_terrazzi: "", cantina: "", superficie_scoperta: "",
          };
        }
        // Migration: ensure superfici.camere exists (locazione parziale).
        if (parsed.superfici && !Array.isArray(parsed.superfici.camere)) {
          parsed.superfici.camere = [];
        }
        return { ...initialData, ...parsed };
      }
    } catch { /* ignore */ }
    return initialData;
  });
  const [submissionId, setSubmissionId] = useState(() => localStorage.getItem("cc_submission_id"));
  const [files, setFiles] = useState([]);
  const [step, setStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  // When the admin requested changes with specific `steps_to_edit`, the form
  // works in "restricted" mode: only those steps are navigable, all others
  // are locked (ProgressSidebar shows a padlock, goNext/goBack skip them).
  // The list is populated by UserPracticeDetail → openInForm via localStorage.
  const [restrictedSteps, setRestrictedSteps] = useState(() => {
    try {
      const raw = localStorage.getItem("cc_restricted_steps");
      if (!raw) return null;
      const arr = JSON.parse(raw);
      return Array.isArray(arr) && arr.length ? arr.map((n) => Number(n)) : null;
    } catch {
      return null;
    }
  });
  const timerRef = useRef(null);

  const setField = useCallback((path, value) => {
    setData((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...cur[parts[i]] };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  }, []);

  // Multi-party helpers (Step 2/3)
  const setLocatori = useCallback((arr) => {
    setData((prev) => ({ ...prev, locatori: arr, locatore: arr[0] || { nome: "", email: "" } }));
  }, []);
  const setConduttori = useCallback((arr) => {
    setData((prev) => ({ ...prev, conduttori: arr, conduttore: { ...(prev.conduttore || {}), nome: arr[0]?.nome || "" } }));
  }, []);

  // Persist locally
  useEffect(() => {
    try { localStorage.setItem("cc_form_data", JSON.stringify(data)); } catch { /* ignore */ }
  }, [data]);

  // Keep legacy mirrors in sync (defensive — for any external setField call)
  useEffect(() => {
    setData((prev) => {
      const firstLoc = prev.locatori?.[0] || { nome: "", email: "" };
      const firstCond = prev.conduttori?.[0] || { nome: "" };
      const needSync =
        prev.locatore?.nome !== firstLoc.nome ||
        prev.locatore?.email !== firstLoc.email ||
        prev.conduttore?.nome !== firstCond.nome;
      if (!needSync) return prev;
      return {
        ...prev,
        locatore: { ...(prev.locatore || {}), nome: firstLoc.nome || "", email: firstLoc.email || "" },
        conduttore: { ...(prev.conduttore || {}), nome: firstCond.nome || "" },
      };
    });
  }, [data.locatori, data.conduttori]);

  // ---- Pertinenze → auto-set Step 7 (Q5, Q6, Q8, Q18, Q19) and Step 8 surface fields ----
  const perts = data.catasto?.pertinenze || [];
  const pertsIncluse = data.catasto?.pertinenze_incluse || [];
  useEffect(() => {
    // Compute per-type sums and presence — combine both arrays for surface sums,
    // but track separately which array contains each type so we can enforce
    // "esclusa = always leased" vs "inclusa = owner may choose to exclude".
    const combined = [...perts, ...pertsIncluse];
    const sums = { box: 0, posto_auto: 0, cantina: 0, balconi_terrazzi: 0, superficie_scoperta: 0 };
    combined.forEach((p) => {
      const mq = Number(p.mq) || 0;
      if (p.tipo === "box") sums.box += mq;
      else if (p.tipo === "posto_auto" || p.tipo === "posto_auto_ricarica") sums.posto_auto += mq;
      else if (p.tipo === "cantina" || p.tipo === "soffitta") sums.cantina += mq;
      else if (p.tipo === "balcone" || p.tipo === "terrazzo") sums.balconi_terrazzi += mq;
      else if (p.tipo === "superficie_scoperta") sums.superficie_scoperta += mq;
    });
    const hasEsclusa = {
      box: perts.some((p) => p.tipo === "box"),
      posto_auto: perts.some((p) => p.tipo === "posto_auto" || p.tipo === "posto_auto_ricarica"),
      cantina: perts.some((p) => p.tipo === "cantina" || p.tipo === "soffitta"),
      balconi_terrazzi: perts.some((p) => p.tipo === "balcone" || p.tipo === "terrazzo"),
      superficie_scoperta: perts.some((p) => p.tipo === "superficie_scoperta"),
    };
    const hasInclusa = {
      box: pertsIncluse.some((p) => p.tipo === "box"),
      posto_auto: pertsIncluse.some((p) => p.tipo === "posto_auto" || p.tipo === "posto_auto_ricarica"),
      cantina: pertsIncluse.some((p) => p.tipo === "cantina" || p.tipo === "soffitta"),
      balconi_terrazzi: pertsIncluse.some((p) => p.tipo === "balcone" || p.tipo === "terrazzo"),
      superficie_scoperta: pertsIncluse.some((p) => p.tipo === "superficie_scoperta"),
    };
    const round2 = (n) => Math.round(n * 100) / 100;

    setData((prev) => {
      const next = { ...prev };
      // Step 8 – lock to combined sum (or 0 if none)
      next.superfici = {
        ...prev.superfici,
        box_garage: String(round2(sums.box)),
        posto_auto: String(round2(sums.posto_auto)),
        cantina: String(round2(sums.cantina)),
        balconi_terrazzi: String(round2(sums.balconi_terrazzi)),
        superficie_scoperta: String(round2(sums.superficie_scoperta)),
      };
      // Step 7 – auto-set corresponding Q using the two-tier rule:
      //   - Pertinenza in `perts` (visura separata): FORCE "si" (always leased)
      //   - Pertinenza only in `pertsIncluse`: SEED "si" if empty, preserve otherwise
      //     (owner may have explicitly chosen "no" — cf. Step 7 requirement 1)
      //   - Not present anywhere: FORCE "no"
      const prevVal = prev.valutazione || {};
      const resolve = (esclusa, inclusa, currentValue) => {
        if (esclusa) return "si";                 // separate visura → always leased
        if (inclusa) {
          if (currentValue === "si" || currentValue === "no") return currentValue;
          return "si";                              // seed default
        }
        return "no";                                // no pertinenza at all
      };
      // Q19 (posto auto) has looser semantics: anche in visura separata, il
      // locatore può decidere di NON locare il posto auto. Quindi "si" è solo
      // un seed, non un lock — preserviamo la scelta esplicita del locatore.
      const resolvePostoAuto = (esclusa, inclusa, currentValue) => {
        if (esclusa || inclusa) {
          if (currentValue === "si" || currentValue === "no") return currentValue;
          return "si";                              // seed default
        }
        return "no";
      };
      next.valutazione = {
        ...prevVal,
        q5: resolve(hasEsclusa.superficie_scoperta, hasInclusa.superficie_scoperta, prevVal.q5),
        q6: resolve(hasEsclusa.balconi_terrazzi, hasInclusa.balconi_terrazzi, prevVal.q6),
        q8: resolve(hasEsclusa.cantina, hasInclusa.cantina, prevVal.q8),
        q18: resolve(hasEsclusa.box, hasInclusa.box, prevVal.q18),
        q19: resolvePostoAuto(hasEsclusa.posto_auto, hasInclusa.posto_auto, prevVal.q19),
      };
      // Step 9 – auto-set cantina_soffitta based on pertinenze presence (either
      // array); Step 9 uses this as a maggiorazione flag, independent of the
      // Step 7 lease-choice.
      next.step9 = {
        ...prev.step9,
        cantina_soffitta: (hasEsclusa.cantina || hasInclusa.cantina) ? "si" : "no",
      };
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(perts), JSON.stringify(pertsIncluse)]);

  // Ensure submission exists
  const ensureSubmission = useCallback(async () => {
    if (submissionId) return submissionId;
    const r = await api.post("/submissions", { data });
    setSubmissionId(r.data.id);
    localStorage.setItem("cc_submission_id", r.data.id);
    return r.data.id;
  }, [submissionId, data]);

  // Save draft to backend (called manually or on step change)
  const saveDraft = useCallback(async () => {
    setIsSaving(true);
    try {
      const id = await ensureSubmission();
      await api.put(`/submissions/${id}`, { data });
      return id;
    } finally {
      setIsSaving(false);
    }
  }, [data, ensureSubmission]);

  // Debounced autosave on data change
  useEffect(() => {
    if (!submissionId) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      api.put(`/submissions/${submissionId}`, { data }).catch((err) => {
        // If the cached submission was already submitted or deleted, reset to start fresh
        const code = err?.response?.status;
        if (code === 403 || code === 404) {
          localStorage.removeItem("cc_submission_id");
          setSubmissionId(null);
        }
      });
    }, 1500);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [data, submissionId]);

  const resetForm = useCallback(() => {
    localStorage.removeItem("cc_form_data");
    localStorage.removeItem("cc_submission_id");
    localStorage.removeItem("cc_restricted_steps");
    localStorage.removeItem("cc_edit_mode");
    setData(initialData);
    setSubmissionId(null);
    setFiles([]);
    setStep(1);
    setRestrictedSteps(null);
  }, []);

  return (
    <FormContext.Provider
      value={{
        data, setData, setField,
        setLocatori, setConduttori,
        step, setStep,
        submissionId, setSubmissionId,
        files, setFiles,
        isSaving, saveDraft, ensureSubmission,
        resetForm,
        restrictedSteps, setRestrictedSteps,
      }}
    >
      {children}
    </FormContext.Provider>
  );
}

export const useForm = () => {
  const ctx = useContext(FormContext);
  if (!ctx) throw new Error("useForm must be used within FormProvider");
  return ctx;
};

// 25 evaluation questions (kept in sync with backend)
export const EVALUATION_QUESTIONS = [
  "Cucina abitabile con almeno 1 finestra",
  "Presenza di ascensore per unità abitative situate al secondo piano o superiore",
  "Impianto di citofono/videocitofono",
  "Impianto di antifurto/allarme/videocamere",
  "Giardino privato o condominiale, spazio ad uso esclusivo o aree verdi",
  "Presenza di balconi, terrazzi o affini",
  "Alloggio dotato di impianto solare/termico, fotovoltaico o cappotto termico",
  "Presenza di cantina o soffitta",
  "Riscaldamento autonomo",
  "Riscaldamento centralizzato",
  "Impianto di climatizzazione e/o raffreddamento",
  "Alloggio o stabile ultimato o completamente ristrutturato negli ultimi 10 anni",
  "Bagno con almeno una finestra dotato di almeno tre elementi",
  "Doppi servizi igienici",
  "Stato di manutenzione e conservazione dell'unità immobiliare buono in tutti i suoi elementi costitutivi",
  "Prossimità dell'alloggio a servizi di trasporto pubblico, esercizi commerciali o affini",
  "Antenna centralizzata/individuale",
  "Box/Garage ad uso esclusivo",
  "Posto auto coperto o scoperto, ad uso esclusivo o in comune",
  "Domotica, automazione di apparecchiature, impianti e sistemi",
  "Porta blindata o barra anti intrusione",
  "Stato di manutenzione e conservazione dello stabile normale in tutti i suoi elementi costitutivi",
  "Doppi/tripli vetri su almeno il 50% delle finestre",
  "Posto auto/Box ad uso esclusivo con ricarica elettrica, in alternativa al punto 18",
  "Installazione Amazon Locker/Key o servizio di portineria",
];

// Pesi per domanda (1-indexed). Distribuzione:
// 1 punto: q3, q6, q10, q13, q17, q19, q21, q22
// 2 punti: q1, q4, q5, q8, q12, q14, q15, q16, q18, q20, q23, q25
// 3 punti: q2, q7, q9, q11, q24
export const QUESTION_WEIGHTS = {
  1: 2, 2: 3, 3: 1, 4: 2, 5: 2, 6: 1, 7: 3, 8: 2, 9: 3, 10: 1,
  11: 3, 12: 2, 13: 1, 14: 2, 15: 2, 16: 2, 17: 1, 18: 2, 19: 1, 20: 2,
  21: 1, 22: 1, 23: 2, 24: 3, 25: 2,
};

export const MAX_SCORE = Object.values(QUESTION_WEIGHTS).reduce((a, b) => a + b, 0);

export const STEP_TITLES = [
  "Introduzione",
  "Proprietario/a Immobile",
  "Conduttore/Conduttrice",
  "Zona e Indirizzo",
  "Estremi Catastali",
  "Tipologia Contratto",
  "Valutazione Immobile",
  "Tipologia Superficie e MQ",
  "Maggiorazioni / Riduzioni",
  "Risultato Calcolo",
  "Carica i Documenti",
  "Dichiarazione Finale",
];

export const DOCUMENT_TYPES = [
  { key: "visura_catastale", label: "Visura Catastale", note: "⚠ La visura deve riportare l'indicazione dei mq. Senza questo dato non sarà possibile procedere con la pratica." },
  { key: "planimetria_catastale", label: "Planimetria Catastale" },
  { key: "ape", label: "Attestato di Prestazione Energetica (APE)", askExpiry: true },
  { key: "contratto_locazione", label: "Contratto di locazione" },
  { key: "scheda_iscrizione", label: "Scheda d'iscrizione" },
  { key: "documento_cf_locatore", label: "Documento e Codice Fiscale — Locatore" },
  { key: "documento_cf_conduttore", label: "Documento e Codice Fiscale — Conduttore" },
  { key: "ricevuta_bonifico", label: "Ricevuta del bonifico bancario a Unioncasa" },
  { key: "ricevuta_bonifico_sindacato", label: "Ricevuta del bonifico all'associazione inquilini" },
  { key: "doc_transitorieta", label: "Documentazione attestante la transitorietà", requiredWhen: "transitorio" },
  { key: "doc_studenti", label: "Iscrizione universitaria o tesserino universitario", requiredWhen: "studenti" },
];

// IBAN Unioncasa (destinatario del bonifico attestazione)
export const UNIONCASA_IBAN = {
  intestatario: "Unioncasa Milano",
  iban: "IT34S0329601601000067790388",
  banca: "Fideuram",
};

// Sindacati inquilini aderenti all'Accordo Territoriale Milano.
// Ordine mostrato in UI = ordine dell'array.
export const SINDACATI_INQUILINI = [
  {
    key: "uniat",
    label: "Uniat",
    intestatario: "UNIAT INQUILINI LOMBARDIA",
    iban: "IT11H0503401738000000010340",
    banca: "",
    importo: 40,
    causale: "Quota attestazione [cognome conduttore]",
  },
  {
    key: "federcasa",
    label: "Feder.Casa",
    intestatario: "Associazione Inquilini Feder.casa Regionale Lombardia",
    iban: "IT06E0840450560000000001466",
    banca: "Banca di Credito Cooperativo",
    importo: 40,
    causale: "CONTRIBUTO STRAORDINARIO",
  },
  {
    key: "ania_inquilini",
    label: "ANIA Inquilini",
    intestatario: "",
    iban: "",
    banca: "",
    importo: null,
    causale: "",
  },
  {
    key: "assocasa",
    label: "ASSOCASA",
    intestatario: "",
    iban: "",
    banca: "",
    importo: null,
    causale: "",
  },
];
